import { useAddSessionBalance } from 'features/add-session-balance';
import { useGetCompanyGroups } from 'features/get-company-groups';
import { useGetCompanyRoles } from 'features/get-company-roles';
import { useMemo, useState } from 'react';
import {
  DateInput,
  Form,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  useShowContext,
  useRefresh,
  useNotify,
  minValue,
  maxValue,
} from 'react-admin';
import { LABELS } from 'shared/constants/labels';
import styled from 'styled-components';
import { SearchCompanyPatientInput } from './search-company-patient-input';

const promocodeExpireDateDefaultValue = new Date(
  new Date().getFullYear(),
  new Date().getMonth() + 6,
).toISOString();

interface AddSessionBalanceFormProps {
  onSuccess?: () => void;
}

const CreateSessionBalanceTargetChoices = [
  { id: 'user', name: 'Пользователь' },
  { id: 'company', name: 'Компания' },
];

export const AddSessionBalanceForm = ({ onSuccess }: AddSessionBalanceFormProps) => {
  const { record } = useShowContext();
  const refresh = useRefresh();
  const notify = useNotify();

  const [target, setTarget] = useState<'user' | 'company' | undefined>();
  const [userIds, setUserIds] = useState<string[]>([]);

  const { addSessionBalances, isLoading: isLoadingAddSessionBalances } = useAddSessionBalance({
    companyId: record.id,
  });
  const { roles, isLoading: isLoadingRoles } = useGetCompanyRoles({
    companyId: record.id,
  });
  const { groups, isLoading: isLoadingGroups } = useGetCompanyGroups({
    companyId: record.id,
  });

  const [rolesChoices, groupsChoices] = useMemo(() => {
    const rolesChoices = roles.map(role => ({ id: role, name: role }));
    const groupsChoices = groups.map(group => ({ id: group, name: group }));

    return [rolesChoices, groupsChoices];
  }, [roles, groups]);

  if (record.isArchive) {
    return (
      <Title>
        Вы не можете добавить баланс консультаций архивированной компании
      </Title>
    );
  }

  const handleSubmit = async (payload: any) => {
    if (!target) {
      notify('Необходимо выбрать цель баланса', { type: 'error' });
      return;
    }

    const sessionBalance = payload;

    if (!sessionBalance) {
      notify('Баланс консультаций не может быть пустым', { type: 'error' });
      return;
    }

    const startWith = new Date(sessionBalance.startWith);
    const endWith = new Date(sessionBalance.endWith);

    if (startWith > endWith) {
      notify('Дата начала должна быть меньше даты окончания', { type: 'error' });
      return;
    }

    if (target === 'user') {
     await addSessionBalances({
        quantityPerUser: sessionBalance.quantityPerUser,
        startWith: startWith.toISOString(),
        endWith: endWith.toISOString(),
        userIds: userIds,
      });
    }

    if (target === 'company') {
      await addSessionBalances({
        quantityPerUser: sessionBalance.quantityPerUser,
        startWith: startWith.toISOString(),
        endWith: endWith.toISOString(),
        companyId: record.id,
        role: !!sessionBalance.role ? sessionBalance.role : undefined,
        group: !!sessionBalance.group ? sessionBalance.group : undefined,
      });
    }

    notify('Балансы консультаций успешно добавлены', { type: 'success' });
    onSuccess?.();
    refresh();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <SelectInput
        isLoading={isLoadingRoles}
        disabled={!roles || roles.length === 0}
        label={LABELS.sessionBalanceCreateTarget}
        source="target"
        fullWidth
        value={target}
        onChange={event => setTarget(event.target.value as 'user' | 'company')}
        choices={CreateSessionBalanceTargetChoices}
        required
      />

      {target === 'user' && (
        <SearchCompanyPatientInput
          companyId={record.id}
          source="userIds"
          label="Поиск пациента по email"
          onChange={value => {
            setUserIds(value);
          }}
        />
      )}

      {target === 'company' && (
        <>
          <SelectInput
            isLoading={isLoadingRoles}
            disabled={!roles || roles.length === 0}
            label={LABELS.companyRole}
            source="role"
            fullWidth
            choices={rolesChoices}
          />
          <SelectInput
            isLoading={isLoadingGroups}
            disabled={!groups || groups.length === 0}
            label={LABELS.companyGroup}
            source="group"
            fullWidth
            choices={groupsChoices}
          />
        </>
      )}

      <NumberInput
        label={LABELS.sessionBalanceQuantityPerUser}
        source="quantityPerUser"
        fullWidth
        validate={[
          required(),
          minValue(1, 'Минимальный значение 1'),
          maxValue(100, 'Максимальный значение 10'),
        ]}
      />
      <DateInput
        label={LABELS.sessionBalanceStartWith}
        source="startWith"
        defaultValue={promocodeExpireDateDefaultValue}
        validate={[
          required(),
          value => {
            if (!value) return undefined;

            const date = new Date(value);
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            return date < now ? 'Дата должна быть в будущем' : undefined;
          },
        ]}
        fullWidth
      />
      <DateInput
        label={LABELS.sessionBalanceEndWith}
        source="endWith"
        defaultValue={promocodeExpireDateDefaultValue}
        validate={[
          required(),
          value => {
            if (!value) return undefined;

            const date = new Date(value);
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            return date < now ? 'Дата должна быть в будущем' : undefined;
          },
        ]}
        fullWidth
      />
      <SaveButton disabled={isLoadingAddSessionBalances} />
    </Form>
  );
};

const Title = styled.h2`
  text-align: center;
  font-family: sans-serif;
  color: #494848;
`;
